<template>
    <section class="SearchContainer">
        <div class="show-and-reset-filter">
            <!-- Cas 1: Filtres déroulés (formulaire visible) -->
            <div v-if="showSearch" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                <div>
                    <el-link @click="showSearch = false">
                        {{ $t("components.searchContainer.hideFilters") }}
                        <em class="el-icon-arrow-up"/>
                    </el-link>
                </div>
                <div class="div-reset-filter">
                    <el-link @click="replaceFilter">{{ $t("general.filters.resetFilters") }}</el-link>
                </div>
            </div>

            <!-- Cas 2: Filtres cachés avec tags personnalisés -->
            <div v-else-if="showManagePinnedFilters || (customFilterButtons && customFilterButtons.length > 0)" style="width: 100%;">
                <!-- En-tête supprimé - plus de lien reset ici -->

                <!-- Champ de recherche et filtres actifs alignés -->
                <div style="display: flex; flex-wrap: wrap; align-items: flex-start; width: 100%; gap: 8px; row-gap: 6px;">
                    <!-- Champ de recherche avec style cohérent -->
                    <div class="filter-search-container">
                        <el-input
                            v-model="quickSearchValue"
                            placeholder="Chercher par ville, titre, référence"
                            size="small"
                            class="filter-search-input"
                            @input="handleSearchInput"
                            @keyup.enter="handleQuickSearch"
                        >
                            <i
                                slot="append"
                                class="el-icon-search"
                                style="cursor: pointer; padding: 0 8px; display: flex; align-items: center;"
                                @click="handleQuickSearch"
                            ></i>
                        </el-input>
                    </div>

                    <div v-for="button in customFilterButtons" :key="button.key" style="position: relative;">

                        <!-- Option 1: Dropdown pour les filtres interactifs SIMPLES (pas date ni objectTypes) -->
                        <el-dropdown
                            v-if="isMultiSelectFilter(button.key) && !isDateFilter(button.key) && !isCustomPopupFilter(button.key)"
                            @click="handleTagClick(button, $event)"
                            trigger="click"
                            :hide-on-click="false"
                            placement="bottom-start"
                            @visible-change="visible => handleDropdownVisibleChange(visible, button.key)"
                            :popper-options="popperOptions"
                        >
                            <el-button
                                size="small"
                                type="default"
                                :class="['filter-dropdown-button', { 'filter-has-value': button.hasValue }]"
                            >
                                <span class="filter-chevron-left">▼</span>
                                {{ button.label }}
                                <i class="el-icon-arrow-down el-icon--right"></i>
                                <i class="el-icon-close filter-close-icon" @click.stop="removeFilter(button.key)"></i>
                            </el-button>
                            <el-dropdown-menu slot="dropdown" style="padding: 0;">
                                <slot :name="`inline-${button.key}`" :close="() => expandedFilter = null"></slot>
                            </el-dropdown-menu>
                        </el-dropdown>

                        <!-- Option 2: Simple button pour les autres (date, objectTypes, etc.) -->
                        <el-button
                            v-else
                            size="small"
                            type="default"
                            :class="['filter-simple-button', { 'filter-has-value': button.hasValue }]"
                            @click="handleTagClick(button, $event)"
                        >
                            <span class="filter-chevron-left">▼</span>
                            {{ button.label }}
                            <i v-if="!button.isMore" class="el-icon-close filter-close-icon" @click.stop="removeFilter(button.key)"></i>
                        </el-button>

        <!-- Overlay transparent pour fermer le popup au clic extérieur -->
        <div v-if="isDateFilter(expandedFilter) || isCustomPopupFilter(expandedFilter)"
             style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 999998;"
             @click="expandedFilter = null">
        </div>

        <!-- DatePicker en popup spécial pour les dates -->
        <div v-if="isDateFilter(expandedFilter)"
             class="filter-date-popup"
             style="position: fixed; z-index: 999999; background: white; border: 1px solid #dcdfe6; border-radius: 4px; padding: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); width: 470px;"
             :style="getPopupPosition(expandedFilter)"
             @click.stop>
           <slot :name="`inline-${expandedFilter}`" :close="() => expandedFilter = null"></slot>
        </div>

        <!-- 🔧 NOUVEAU : Popup personnalisé pour objectTypes (cascader) avec positionnement intelligent -->
        <div v-if="isCustomPopupFilter(expandedFilter)"
             class="filter-custom-popup"
             style="position: fixed; z-index: 999999; background: white; border: 1px solid #dcdfe6; border-radius: 4px; padding: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15);"
             :style="getPopupPosition(expandedFilter)"
             @click.stop>
           <slot :name="`inline-${expandedFilter}`" :close="() => expandedFilter = null"></slot>
        </div>
                    </div>

                    <!-- Lien "Plus..." pour gérer les filtres épinglés -->
                    <div v-if="showManagePinnedFilters" style="position: relative;">
                        <el-link
                            type="primary"
                            @click="handleManagePinnedFiltersClick"
                            style="font-size: 14px; margin-left: 8px;margin-top: 8px;"
                        >
                            Plus...
                        </el-link>
                    </div>
                </div>
            </div>

            <!-- Cas 3: Filtres cachés sans tags (lien standard) -->
            <div v-else>
                <el-link @click="showSearch = true">
                    <span style="text-transform: uppercase">
                      <strong>
                        {{ $t("components.searchContainer.showFilters") }}
                      </strong>
                    </span>
                    <em class="el-icon-arrow-down"/>
                </el-link>
            </div>
        </div>

        <div v-show="showSearch" style="margin-top: 10px">
            <div class="SearchContainer-main">
                <slot></slot>
                <el-button native-type="submit" type="danger">
                    {{ $t("components.searchContainer.search") }}

                </el-button>
            </div>
            <div v-if="filters" class="SearchContainer-filters">
                <br/>
                <el-radio-group v-model="filtersRadio">
                    <template v-for="({ value, label }, index) in filters">
                        <el-divider
                                direction="vertical"
                                :key="index + '-divider'"
                                v-if="index > 0"
                        />
                        <el-radio class="SearchContainer-filter" :label="label" :value="value"
                                  @change="$emit('filter', value)"></el-radio>
                        <!--          <el-button-->
                        <!--            class="SearchContainer-filter el-button&#45;&#45;text&#45;&#45;danger"-->
                        <!--            type="text"-->
                        <!--            v-text="label"-->
                        <!--            :key="value"-->
                        <!--            @click="$emit('filter', value)"-->
                        <!--          />-->
                    </template>
                </el-radio-group>
            </div>
            <el-collapse
                    class="SearchContainer-more"
                    v-if="$slots.more"
                    accordion
                    :value="moreOpened ? 'advanced' : null"
                    @change="item => $emit('update:more-opened', item === 'advanced')"
            >
                <el-collapse-item name="advanced" :title="$t('components.searchContainer.advancedSearch')">
                    <slot name="more"></slot>
                </el-collapse-item>
            </el-collapse>
        </div>

    </section>
</template>

<script>
import { Collapse, CollapseItem, Divider, Tag, Dropdown, DropdownMenu, DropdownItem, ButtonGroup } from 'element-ui'
import { mapMutations } from 'vuex'
import ClickOutside from 'element-ui/src/utils/clickoutside'

/**
     * Conteneur pour la recherche d'éléments
     */
export default {
  name: 'UbiSearchContainer',
  props: {
    /**
             * Indique si le filtre est effectué en temps réel (et masque le bouton Rechercher)
             */
    realtime: Boolean,
    /**
             * Indique si le panneau de recherche avancée est ouvert par défaut
             */
    moreOpened: Boolean,
    /**
             * Liste des filtres
             */
    filters: Array,
    defaultFilter: String,
    /**
             * Boutons de filtres personnalisés à afficher à la place du lien "Afficher les filtres"
             */
    customFilterButtons: Array,
    /**
             * Indique si le bouton "Gérer les filtres épinglés" doit être affiché
             */
    showManagePinnedFilters: Boolean,
    /**
             * Valeur actuelle du champ de recherche libre
             */
    searchValue: String
  },
  watch: {
    defaultFilter: function (newFilter) {
      const obj = this.filters.find(o => o.value === newFilter)
      if (obj) {
        this.filtersRadio = obj.label
        const valueToEmit = obj.value
        // Ne pas émettre pendant l'initialisation pour éviter les race conditions
        if (this.isInitialized) {
          this.$emit('filter', valueToEmit)
        }
      } else {
        if (this.filters.length > 0) {
          this.filtersRadio = this.filters[0].label
        }
      }
    },
    searchValue: function (newValue) {
      // Synchroniser la valeur de recherche avec la prop
      this.quickSearchValue = newValue || ''
    }
  },
  data () {
    return {
      showSearch: false,
      filtersRadio: null,
      defaultRadio: null,
      quickSearchValue: '',
      expandedFilter: null,
      tagPosition: null,
      isInitialized: false
    }
  },
  computed: {
    // 🔧 Configuration Popper.js pour gérer le débordement automatiquement
    popperOptions() {
      return {
        modifiers: {
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
            padding: 10,
            escapeWithReference: true // Permet au popup de sortir des limites du bouton de référence
          },
          flip: {
            enabled: true,
            behavior: ['bottom-start', 'bottom-end', 'top-start', 'top-end']
          },
          offset: {
            offset: '0,4'
          }
        }
      }
    }
  },
  mounted() {
    // Initialiser la valeur de recherche avec la prop
    this.quickSearchValue = this.searchValue || ''

    // Permettre l'émission d'événements après le montage
    this.$nextTick(() => {
      this.isInitialized = true
    })
  },
  methods: {
    setDefaultFilter (value) {
      this.defaultFilter = value
    },
    async replaceFilter () {
      // this.filtersRadio = this.defaultRadio;
      // this.defaultFilter = this.defaultRadio;
      this.$emit('resetfilter')
    },
    handleSearchInput () {
      // Émettre les changements de saisie sans déclencher la recherche
      this.$emit('search-input', this.quickSearchValue)
    },
    handleQuickSearch () {
      this.$emit('quick-search', this.quickSearchValue)
    },
    removeFilter (filterKey) {
      console.log('Removing filter:', filterKey);
      this.$emit('remove-filter', filterKey)
    },
    handleTagClick (button, event) {
      console.log(' [SearchContainer.handleTagClick] Button clicked:', button.key, button)
      console.log(' [SearchContainer.handleTagClick] isMultiSelectFilter:', this.isMultiSelectFilter(button.key))
      console.log(' [SearchContainer.handleTagClick] isDateFilter:', this.isDateFilter(button.key))
      console.log(' [SearchContainer.handleTagClick] isCustomPopupFilter:', this.isCustomPopupFilter(button.key))

      if (this.isMultiSelectFilter(button.key)) {
        console.log(' [SearchContainer.handleTagClick] Traitement MultiSelectFilter pour:', button.key)
        // Basculer l'affichage du sélecteur en ligne
        const previousExpandedFilter = this.expandedFilter
        this.expandedFilter = this.expandedFilter === button.key ? null : button.key

        // Si on ferme un filtre, émettre l'événement filter-closed
        if (previousExpandedFilter === button.key && this.expandedFilter === null) {
          console.log('Filter closed by click:', button.key)
          this.$emit('filter-closed', button.key)
        }

        // Stocker la position du tag cliqué pour positionner le popup
        if (this.expandedFilter === button.key && event && event.target) {
          this.$nextTick(() => {
            const rect = event.target.getBoundingClientRect()
            this.tagPosition = {
              top: rect.bottom + 4,
              left: rect.left,
              width: rect.width
            }

            // 🔧 NOUVEAU : Pour les popups personnalisés, ajuster après le rendu
            if (this.isCustomPopupFilter(button.key)) {
              // Double nextTick pour laisser le temps au cascader de calculer sa largeur
              this.$nextTick(() => {
                this.$nextTick(() => {
                  setTimeout(() => {
                    this.adjustCustomPopupPosition(button.key)
                  }, 50)
                })
              })
            }
          })
        }
      } else {
        // Pour les filtres simples, ouvrir le panneau complet
        this.showSearch = true
      }
    },

    // 🔧 NOUVEAU : Ajuster la position du popup personnalisé après le rendu
    adjustCustomPopupPosition (filterKey) {
      const popup = document.querySelector('.filter-custom-popup')
      if (!popup) return

      // Obtenir la largeur réelle du popup après rendu
      const popupWidth = popup.offsetWidth
      const viewportWidth = window.innerWidth
      let leftPosition = this.tagPosition.left

      // Si le popup déborde à droite, le décaler
      if (leftPosition + popupWidth > viewportWidth - 20) {
        leftPosition = viewportWidth - popupWidth - 20

        if (leftPosition < 20) {
          leftPosition = 20
        }

        // Recalculer la position de la flèche
        const arrowOffset = this.tagPosition.left - leftPosition + 20

        // Appliquer les styles
        popup.style.left = leftPosition + 'px'
        popup.style.setProperty('--arrow-left', arrowOffset + 'px')
      }
    },
    isMultiSelectFilter (filterKey) {
      //  GÉNÉRIQUE : Utiliser la config backend au lieu de logique hardcodée
      const button = this.customFilterButtons.find(b => b.key === filterKey)
      if (button && button.field) {
        const renderType = button.field.properties && button.field.properties.renderType
        const fieldType = button.field.fieldType && button.field.fieldType.code

        // Si renderType est explicitement défini, l'utiliser
        if (renderType === 'popup' || renderType === 'dropdown') {
          return true
        }

        // Sinon, déduire du type de champ
        const multiSelectTypes = ['radio_group', 'select', 'multiselect', 'daterange', 'range']
        return multiSelectTypes.includes(fieldType)
      }

      return false
    },
    isDateFilter (filterKey) {
      //  GÉNÉRIQUE : Utiliser la config backend au lieu de logique hardcodée
      const button = this.customFilterButtons.find(b => b.key === filterKey)
      if (button && button.field) {
        const fieldType = button.field.fieldType && button.field.fieldType.code
        return fieldType === 'daterange'
      }

      return false
    },
    isCustomPopupFilter (filterKey) {
      //  GÉNÉRIQUE : Utiliser la config backend au lieu de logique hardcodée
      const button = this.customFilterButtons.find(b => b.key === filterKey)
      if (button && button.field) {
        const renderType = button.field.properties && button.field.properties.renderType
        const component = button.field.component

        // Si c'est explicitement marqué comme custom popup
        if (renderType === 'custom_popup') {
          return true
        }

        // Ou si c'est un composant spécialisé qui nécessite un positionnement custom
        if (component === 'SearchFieldObjectTypes') {
          return true
        }

        return false
      }

      return false
    },
    getPopupPosition (filterKey) {
      // Utiliser position fixed avec z-index très élevé pour être au premier plan
      const baseStyle = {
        position: 'fixed',
        zIndex: 999999
        //  Ne pas définir min-width/max-width ici, c'est géré par le style inline du template
      }

      // Si on a la position du tag, l'utiliser
      if (this.tagPosition) {
        // 🔧 CORRECTION : Détecter le débordement et ajuster la position
        const viewportWidth = window.innerWidth
        const popupWidth = this.getPopupWidthForFilter(filterKey) // Largeur estimée du popup
        let leftPosition = this.tagPosition.left

        // Position de la flèche (relative au bouton)
        const arrowOffset = this.tagPosition.left - leftPosition + 20

        // Si le popup déborde à droite, le décaler vers la gauche
        if (leftPosition + popupWidth > viewportWidth - 20) {
          // Aligner le popup à droite du viewport avec une marge de 20px
          leftPosition = viewportWidth - popupWidth - 20

          // S'assurer qu'on ne déborde pas à gauche non plus
          if (leftPosition < 20) {
            leftPosition = 20
          }
        }

        // Calculer la position de la flèche pour qu'elle reste alignée avec le bouton
        const finalArrowOffset = this.tagPosition.left - leftPosition + 20

        return {
          ...baseStyle,
          top: this.tagPosition.top + 'px',
          left: leftPosition + 'px',
          '--arrow-left': finalArrowOffset + 'px' // Variable CSS pour positionner la flèche
        }
      }

      return baseStyle
    },

    // 🔧 CORRECTION : Helper pour obtenir la largeur estimée d'un popup
    getPopupWidthForFilter (filterKey) {
      // Largeurs estimées des différents types de popups
      const widths = {
        'dateCreation': 470,
        'date': 470,
        'objectTypes': 270, // Pour le cascader (largeur minimale : 1 colonne + padding)
        'price': 420,
        'surface': 380,
        'default': 300
      }
      return widths[filterKey] || widths.default
    },
    handleDropdownVisibleChange (visible, filterKey) {
      // Quand un dropdown se ferme, émettre l'événement filter-closed
      if (!visible) {
        console.log('Dropdown closed for filter:', filterKey)
        this.$emit('filter-closed', filterKey)
      }
    },
    handleManagePinnedFiltersClick () {
      console.log('Manage pinned filters clicked in SearchContainer')
      this.$emit('manage-pinned-filters')
    }
  },
  components: {
    ElCollapse: Collapse,
    ElCollapseItem: CollapseItem,
    ElDivider: Divider,
    ElTag: Tag,
    ElDropdown: Dropdown,
    ElDropdownMenu: DropdownMenu,
    ElDropdownItem: DropdownItem,
    ElButtonGroup: ButtonGroup
  },
  directives: {
    ClickOutside
  },
  beforeMount () {
    this.filtersRadio = null
    this.defaultRadio = this.defaultValue

    if (!this.filters || this.filters.length === 0) {
      return
    }

    let valueToEmit = this.filters[0].value

    if (this.defaultFilter) {
      const obj = this.filters.find(o => o.value === this.defaultFilter)
      if (obj && obj.label) {
        this.filtersRadio = obj.label
        valueToEmit = obj.value
      } else {
        this.filtersRadio = this.filters[0].label
      }
    } else {
      this.filtersRadio = this.filters[0].label
    }

    this.$emit('filter', valueToEmit)
  }
}
</script>

<style lang="scss">
    @import "@/theme/_bootstrap.scss";

    .SearchContainer {
        &-main {
            > *:not(:last-child) {
                @extend .mr-4;

                &:first-child {
                    flex: 1;
                }
            }
        }

        &-filters {
            @extend .py-2;
            font-size: $font-size-sm;

            .el-button {
                font-size: $font-size-sm-alt;
            }
        }

        .el-checkbox {
            align-items: center;
            display: flex;
            height: 40px;
            justify-content: center;
            line-height: 40px;
            margin-bottom: 0;
        }

      .show-and-reset-filter {
        display: flex;
        justify-content: space-between;
        .div-reset-filter{
          text-align: end;
        }
      }

      &-prepend {
            @extend .mr-3;
        }

        &-filter {
            @extend .py-0;
        }

        &-more {
            &,
            .el-collapse-item__header,
            .el-collapse-item__wrap {
                @extend .border-0;
            }

            .el-collapse-item__header {
                @extend .text-primary;
                font-size: $font-size-base;

                display: flex;
                flex-direction: row-reverse;
                justify-content: flex-end;
                align-items: center;

                .el-collapse-item__arrow {
                    @extend .ml-0;
                }
            }
        }

        @include media-breakpoint-up(lg) {
            &-main {
                display: flex;
                align-items: flex-end;
                justify-content: space-between;

                .FormItem {
                    padding-bottom: 0 !important;
                }
            }
        }

        @include media-breakpoint-down(sm) {
            &-prepend {
                display: block;
                margin-bottom: map-get($spacers, 1) !important;
            }
            &-filters {
                margin-top: map-get($spacers, 2) !important;

                .el-divider {
                    display: none;
                }
            }
            &-filter {
                display: block;
                margin-bottom: map-get($spacers, 2) !important;
                margin-left: map-get($spacers, 2) !important;
            }

            &-main {
                > *:not(:last-child) {
                    margin-right: 0 !important;
                                 }
             }
         }

        /* Popup de date avec flèche */
        ::v-deep .filter-date-popup {
            font-size: 14px !important;
            position: relative !important;
            overflow: visible !important;

            .el-date-picker {
                font-size: 14px !important;

                .el-input__inner {
                    font-size: 14px !important;
                    height: 36px !important;
                    line-height: 36px !important;
                }
            }

            .el-button {
                font-size: 14px !important;
                padding: 8px 16px !important;
                min-height: 32px !important;
            }

            .el-form-item {
                margin-bottom: 12px !important;

                .el-form-item__label {
                    font-size: 14px !important;
                    line-height: 1.4 !important;
                }
            }
        }
     }

    /* Styles pour les boutons de filtres et popups */
    .filter-buttons-container {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        row-gap: 6px;
        margin-bottom: 12px;
    }

    .filter-button-container {
        position: relative;
    }

    /* Assurer que les popups sont au premier plan */
    .filter-button-container > div[style*="z-index"] {
        position: fixed !important;
        z-index: 999999 !important;
        background: white !important;
        border: 1px solid #dcdfe6 !important;
        border-radius: 4px !important;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
    }

    /* Styles pour les nouveaux composants dropdown et button avec charte graphique */
    .SearchContainer {
        /* Styles pour les boutons dropdown - couleurs par défaut */
        .filter-dropdown-button {
            font-size: 14px !important;
            padding: 8px 16px !important;
            position: relative;
            font-weight: 500 !important;
            min-height: 32px !important;

            .el-icon-arrow-down {
                font-size: 12px;
                margin-left: 6px;
                margin-right: 8px;
            }
        }

        /* Styles pour les boutons simples - couleurs par défaut */
        .filter-simple-button {
            font-size: 14px !important;
            padding: 8px 16px !important;
            font-weight: 500 !important;
            min-height: 32px !important;
        }

        /*  Styles pour les filtres avec valeur sélectionnée - COULEUR CHARTE #0ab1bf */
        .filter-dropdown-button.filter-has-value,
        .filter-simple-button.filter-has-value {
            border-left: 4px solid #0ab1bf !important;
            background: rgba(10, 177, 191, 0.15) !important;
            border-color: #0ab1bf !important;
            color: #087b8a !important;
            font-weight: 600 !important;

            /* Transition douce */
            transition: all 0.2s ease !important;
        }

        /* Effet hover pour les filtres avec valeur */
        .filter-dropdown-button.filter-has-value:hover,
        .filter-simple-button.filter-has-value:hover {
            background: rgba(10, 177, 191, 0.22) !important;
            border-color: #087b8a !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 2px 8px rgba(10, 177, 191, 0.3) !important;
        }

        /* Styles pour les chevrons à gauche - PLUS GROS */
        .filter-chevron-left {
            font-size: 14px !important;
            margin-right: 8px !important;
            margin-left: -2px !important;
            color: #909399 !important;
            opacity: 0.9 !important;
            display: inline-block !important;
            vertical-align: middle !important;
            line-height: 1 !important;
            font-weight: normal !important;
        }

        /* Hover sur les boutons pour faire ressortir le chevron */
        .filter-dropdown-button:hover .filter-chevron-left,
        .filter-simple-button:hover .filter-chevron-left {
            color: #606266 !important;
            opacity: 1 !important;
            transform: scale(1.1) !important;
        }

        /* Conteneur du champ de recherche pour harmoniser avec les boutons */
        .filter-search-container {
            position: relative;
            display: inline-block;
        }

        /* Styles pour le champ de recherche harmonisé avec les boutons */
        .filter-search-input {
            min-width: 280px !important;
            max-width: 320px !important;

            .el-input__inner {
                font-size: 14px !important;
                padding: 8px 12px !important;
                height: 32px !important;
                min-height: 32px !important;
                line-height: 1.4 !important;
                font-weight: 500 !important;
                border: 1px solid #606266 !important;
                border-radius: 4px 0 0 4px !important;
                background: #ffffff !important;
                transition: border-color 0.2s, box-shadow 0.2s !important;

                &::placeholder {
                    color: #909399 !important;
                    font-weight: normal !important;
                }

                &:hover {
                    border-color: #909399 !important;
                }

                &:focus {
                    border-color: #409eff !important;
                    outline: none !important;
                    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2) !important;
                }
            }

            .el-input-group__append {
                padding: 0 !important;
                border: 1px solid #606266 !important;
                border-left: none !important;
                background: #ffffff !important;
                border-radius: 0 4px 4px 0 !important;
                transition: border-color 0.2s !important;

                .el-icon-search {
                    color: #909399 !important;
                    font-size: 14px !important;
                    transition: color 0.2s !important;

                    &:hover {
                        color: #409eff !important;
                    }
                }
            }

            &:hover .el-input-group__append {
                border-color: #909399 !important;
            }

            /* Hover sur toute la zone de recherche */
            &:hover {
                .el-input__inner {
                    border-color: #909399 !important;
                }
                .el-input-group__append {
                    border-color: #909399 !important;
                }
            }
        }





        /* Styles pour les icônes de suppression - couleurs par défaut */
        .filter-close-icon {
            font-size: 12px !important;
            margin-left: 10px !important;
            cursor: pointer !important;
            opacity: 0.6 !important;
            transition: all 0.2s !important;
            color: #606266 !important;

            &:hover {
                opacity: 1 !important;
                color: #d24554 !important;
                transform: scale(1.2) !important;
            }
        }

        /* Bouton de suppression dans le dropdown */
        .filter-remove-button {
            color: #d24554 !important;
            font-size: 14px !important;
            padding: 8px 16px !important;
            min-height: 32px !important;

            &:hover {
                background: #ffedef !important;
                color: #d24554 !important;
            }
        }

        /* Menu dropdown personnalisé */
        .el-dropdown-menu {
            border: 1px solid #e4e7ed;
            box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
            min-width: 250px !important;
            max-width: 400px !important;

            .el-dropdown-item {
                font-size: 14px !important;
                padding: 8px 16px !important;
                line-height: 1.4 !important;
                white-space: nowrap !important;

                &:not(:last-child) {
                    border-bottom: 1px solid #f4f4f5;
                }
            }

            /* Contenu du dropdown (slot) */
            > div {
                font-size: 14px !important;
                padding: 8px !important;
                min-width: 250px !important;
                max-width: 400px !important;

                /* Styles pour les éléments de formulaire dans le dropdown */
                .el-checkbox {
                    font-size: 14px !important;
                    margin-bottom: 8px !important;

                    .el-checkbox__label {
                        font-size: 14px !important;
                        line-height: 1.4 !important;
                        padding-left: 8px !important;
                        white-space: nowrap !important;
                    }
                }

                .el-radio {
                    font-size: 14px !important;
                    margin-bottom: 8px !important;

                    .el-radio__label {
                        font-size: 14px !important;
                        line-height: 1.4 !important;
                        padding-left: 8px !important;
                        white-space: nowrap !important;
                    }
                }

                .el-select {
                    font-size: 14px !important;

                    .el-input__inner {
                        font-size: 14px !important;
                        height: 36px !important;
                        line-height: 36px !important;
                    }
                }

                .el-input {
                    font-size: 14px !important;

                    .el-input__inner {
                        font-size: 14px !important;
                        height: 36px !important;
                        line-height: 36px !important;
                    }
                }

                /* Boutons dans le dropdown */
                .el-button {
                    font-size: 14px !important;
                    padding: 8px 16px !important;
                    min-height: 32px !important;
                }
            }
        }
    }

</style>


<docs>
    ### Exemples

    ```javascript
    <ubi-panel style="background-color: #EBF3F2;">
        <ubi-search-container>
            <el-form-item label="Recherche">
                <el-input type="text" placeholder="Champ de recherche"/>
            </el-form-item>

            <el-form-item label="Recherche 2">
                <el-input type="text" placeholder="Champ de recherche 2"/>
            </el-form-item>

            <template v-slot:more>
                <el-form-item label="Champ de recherche avancée">
                    <el-input placeholder="Champ de recherche"/>
                </el-form-item>
            </template>
        </ubi-search-container>
    </ubi-panel>
    ```
</docs>

<style>
/* Style global pour la flèche du popup de date - TAILLE ELEMENT UI STANDARD */
.filter-date-popup::before {
    content: '' !important;
    position: absolute !important;
    top: -12px !important;
    left: 20px !important;
    width: 0 !important;
    height: 0 !important;
    border-left: 12px solid transparent !important;
    border-right: 12px solid transparent !important;
    border-bottom: 12px solid #dcdfe6 !important;
    z-index: 1 !important;
}

.filter-date-popup::after {
    content: '' !important;
    position: absolute !important;
    top: -11px !important;
    left: 21px !important;
    width: 0 !important;
    height: 0 !important;
    border-left: 11px solid transparent !important;
    border-right: 11px solid transparent !important;
    border-bottom: 11px solid white !important;
    z-index: 2 !important;
}

/* 🔧 NOUVEAU : Flèche pour les popups personnalisés (objectTypes) avec positionnement dynamique */
.filter-custom-popup::before {
    content: '' !important;
    position: absolute !important;
    top: -12px !important;
    left: var(--arrow-left, 20px) !important; /* Utilise la variable CSS calculée */
    width: 0 !important;
    height: 0 !important;
    border-left: 12px solid transparent !important;
    border-right: 12px solid transparent !important;
    border-bottom: 12px solid #dcdfe6 !important;
    z-index: 1 !important;
}

.filter-custom-popup::after {
    content: '' !important;
    position: absolute !important;
    top: -11px !important;
    left: calc(var(--arrow-left, 20px) + 1px) !important; /* Offset de 1px pour l'alignement */
    width: 0 !important;
    height: 0 !important;
    border-left: 11px solid transparent !important;
    border-right: 11px solid transparent !important;
    border-bottom: 11px solid white !important;
    z-index: 2 !important;
}
</style>
